import { useCallback, useEffect, useRef } from 'react';
import { Subscription } from 'rxjs';
import { IGenericSocketUpdate } from '../../data-models/socket-base.data-model';
import { WebSocketService } from '../WebSocketService';
import { useToken } from './useToken';

interface IUseSocketEventSubscriptionOptions {
  /** When false, tears down any existing subscription and does not resubscribe. */
  enabled?: boolean;
  /** Replay the last received value to a new subscriber (third arg of `listen`). */
  replayLast?: boolean;
}

/**
 * Shared lifecycle for consuming one namespaced socket event: init, listen, hold
 * the Subscription, tear down on unmount. `initService` is async while cleanup is
 * sync, so an epoch counter drops any listener assignment that resolves after the
 * effect was torn down — avoids orphaned listeners and duplicate handlers. The
 * handler is read through a ref so a new closure per render does not resubscribe.
 */
export function useSocketEventSubscription<TResponse extends IGenericSocketUpdate<unknown>>(
  eventName: string,
  handler: (update: TResponse) => void,
  { enabled = true, replayLast = false }: IUseSocketEventSubscriptionOptions = {}
): void {
  const listenerRef = useRef<Subscription | null>(null);
  const epochRef = useRef(0);
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  const getToken = useToken();

  const subscribe = useCallback(() => {
    listenerRef.current?.unsubscribe();
    listenerRef.current = null;
    const epoch = ++epochRef.current;

    WebSocketService.initService(getToken).then(() => {
      // Dropped if superseded by a newer subscribe or torn down before resolving.
      if (epoch !== epochRef.current) {
        return;
      }
      listenerRef.current = WebSocketService.get().listen<TResponse>(
        eventName,
        (update) => handlerRef.current(update),
        replayLast
      );
    });
  }, [eventName, getToken, replayLast]);

  const unsubscribe = useCallback(() => {
    epochRef.current++;
    listenerRef.current?.unsubscribe();
    listenerRef.current = null;
  }, []);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    subscribe();
    return unsubscribe;
  }, [enabled, subscribe, unsubscribe]);
}
